import { ActionTree, MutationTree } from 'vuex'
import { AxiosResponse } from 'axios'
import { magnoliaServiceAPI } from '@/store/api-instance'
import { IRootState } from '@/store'
import {
  ILinksState,
  IFetchLinksRequest,
  IFetchLinksResponse,
  LinksMutationType,
  ILinksMutations,
  LinksActionType,
  ILinksActions,
  ILinks,
  IFetchPortalHardCodeLinkRequest,
  IFetchPortalHardCodeLinkResponse,
  IFetchPortalPageMenuLinkResponse,
  IFetchPortalPageMenuLinkRequest,
  IPortalPageMenuLinkActions,
  IPortalHardCodeLinkActions,
  IPortalHardCodeLinkMutations,
  IPortalHardCodeLinkState,
  IPortalPageMenuLinkState,
  IPortalPageMenuLinkMutations
} from './interfaces'

const linksState = (): ILinksState => ({
  links: [],
  loading: false
})

const portalState = (): IPortalHardCodeLinkState => ({
  portalHardCodeLink: {
    total: 0,
    offset: 0,
    limit: 0,
    results: []
  },
  search: '',
  loading: false
})

const portalPageMenuState = (): IPortalPageMenuLinkState => ({
  portalPageMenuLink: {
    items: []
  }
})

const fetchLinks = async (dto: IFetchLinksRequest): Promise<IFetchLinksResponse> => {
  const response: AxiosResponse = await magnoliaServiceAPI.get<IFetchLinksResponse>('links', { params: dto })
  return response.data
}

const pageMenuLinks = async (dto: IFetchPortalPageMenuLinkRequest): Promise<IFetchPortalPageMenuLinkResponse> => {
  const response: AxiosResponse = await magnoliaServiceAPI.get<IFetchPortalPageMenuLinkResponse>('pagemenu', {
    params: dto
  })
  return response.data
}

const portalHardCodeLinkAPI = async (
  dto: IFetchPortalHardCodeLinkRequest
): Promise<IFetchPortalHardCodeLinkResponse> => {
  const response: AxiosResponse = await magnoliaServiceAPI.get<IFetchPortalHardCodeLinkResponse>('portalExtLinks', {
    params: dto
  })
  return response.data
}

const fetchAllLinks = async (dto: IFetchLinksRequest, oldData: Array<ILinks> = []): Promise<Array<ILinks>> => {
  const { results, total, limit } = await fetchLinks(dto)

  if (results?.length) {
    oldData = (oldData || []).concat(results)
  }

  if (total > oldData.length) {
    return await fetchAllLinks({ ...dto, offset: oldData.length, limit }, oldData)
  }

  return oldData
}

const mutations: MutationTree<ILinksState & IPortalHardCodeLinkState & IPortalPageMenuLinkState> &
  ILinksMutations &
  IPortalHardCodeLinkMutations &
  IPortalPageMenuLinkMutations = {
    [LinksMutationType.SET_LOADING] (
      state: ILinksState & IPortalHardCodeLinkState & IPortalPageMenuLinkState,
      payload: boolean
    ) {
      state.loading = payload
    },
    [LinksMutationType.SET_ALL_LINKS] (state: ILinksState & IPortalHardCodeLinkState, payload: Array<ILinks>) {
      state.links = payload
    },
    [LinksMutationType.SET_PORTAL_HARD_CODE_LINK] (
      state: ILinksState & IPortalHardCodeLinkState,
      payload: IFetchPortalHardCodeLinkResponse
    ) {
      state.portalHardCodeLink = payload
    },
    [LinksMutationType.SET_PAGE_MENU_LINK] (
      state: ILinksState & IPortalPageMenuLinkState,
      payload: IFetchPortalPageMenuLinkResponse
    ) {
      state.portalPageMenuLink = payload
    }
  }

const actions: ActionTree<ILinksState & IPortalHardCodeLinkState & IPortalPageMenuLinkState, IRootState> &
  ILinksActions &
  IPortalHardCodeLinkActions &
  IPortalPageMenuLinkActions = {
    async [LinksActionType.FETCH_ALL_LINKS] ({ commit }, dto: IFetchLinksRequest) {
      commit(LinksMutationType.SET_LOADING, true)
      const data = await fetchAllLinks(dto)
      commit(LinksMutationType.SET_ALL_LINKS, data)
      commit(LinksMutationType.SET_LOADING, false)
    },
    async [LinksActionType.FETCH_PORTAL_HARD_CODE_LINK] ({ commit }, dto: IFetchPortalHardCodeLinkRequest) {
      commit(LinksMutationType.SET_LOADING, true)
      const data = await portalHardCodeLinkAPI(dto)
      commit(LinksMutationType.SET_PORTAL_HARD_CODE_LINK, data)
      commit(LinksMutationType.SET_LOADING, false)
    },
    async [LinksActionType.FETCH_PAGE_MENU_LINK] ({ commit }, dto: IFetchPortalPageMenuLinkRequest) {
      commit(LinksMutationType.SET_LOADING, true)
      const data = await pageMenuLinks(dto)
      commit(LinksMutationType.SET_PAGE_MENU_LINK, data)
      commit(LinksMutationType.SET_LOADING, false)
    }
  }

export default {
  linksState,
  portalState,
  portalPageMenuState,
  mutations,
  actions
}
